/**
 * FontesConsultadas — o que a resposta do @Atendente CONSULTOU na Base de
 * Conhecimento (TASK-11825, spec 2026-09-26-atendente-fontes-estruturadas §7).
 *
 * Usado nas duas telas que gravam o dado: o chat de conversas
 * (`metadata.fontes` da mensagem do bot) e o chat avulso do assistente
 * (`fontes` do GET de detalhe). Quatro decisões do desenho:
 *
 * 1. **"Consultou", nunca "comprovou".** A resposta que abre com a frase fixa
 *    de falta de lastro também grava o que leu (§2).
 * 2. **O título se resolve na LEITURA, por quem lê.** A mensagem pode estar
 *    numa conversa de grupo, e o `metadata` só traz `document_id` e página. A
 *    rota aplica a regra de pasta por usuário; o que o leitor não lê volta sem
 *    título e aparece como "Documento indisponível" — não é omitido, senão a
 *    lista mentiria sobre o lastro da resposta (§7.3).
 * 3. **Fechado por padrão**; o título só é pedido ao abrir. Conversa de grupo
 *    com dezenas de respostas não dispara dezenas de chamadas no carregamento.
 * 4. **Sem registro, nada aparece.** Mensagem anterior à TASK-9885 não tem
 *    `fontes`, e ausência do registro não é "nenhuma fonte consultada".
 *
 * Fonte disponível abre o visualizador sem cópia na primeira página citada
 * (TASK-11856, spec 2026-09-27-kb-visualizar-sem-copia §11). Quem decide se a
 * página vale é o visualizador: em Markdown e DOCX a página citada é da extração,
 * não do PDF diagramado, e ele abre na 1.
 */
import { useState } from 'react'
import { Box, Group, Loader, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconBooks, IconChevronDown, IconChevronRight, IconFileText, IconLock } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'

import { resolverFontesKb } from '@/api/kb'
import { VisualizadorDeDocumento } from '@/components/kb/VisualizadorDeDocumento'
import { FONTE_DADO } from '@/pages/chat/components/Dado'

export interface FonteRegistrada {
  document_id: number
  pagina: number | null
}

export interface FontesDaMensagem {
  fontes: FonteRegistrada[]
  /** Pares `(documento, página)` antes do corte a 40; só existe quando cortou. */
  total: number | null
}

function inteiro(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v)
}

/**
 * Lê `fontes`/`fontes_total` do jeito que chegam (metadata ou detalhe). Tolerante:
 * item sem `document_id` inteiro é descartado; nada válido devolve `null`.
 */
export function lerFontes(fontes: unknown, fontesTotal?: unknown): FontesDaMensagem | null {
  if (!Array.isArray(fontes)) return null
  const validas: FonteRegistrada[] = []
  for (const f of fontes) {
    if (!f || typeof f !== 'object' || Array.isArray(f)) continue
    const { document_id, pagina } = f as Record<string, unknown>
    if (!inteiro(document_id)) continue
    validas.push({ document_id, pagina: inteiro(pagina) ? pagina : null })
  }
  if (validas.length === 0) return null
  const total = inteiro(fontesTotal) && fontesTotal > validas.length ? fontesTotal : null
  return { fontes: validas, total }
}

interface DocumentoAgrupado {
  document_id: number
  paginas: number[]
}

function agrupar(fontes: FonteRegistrada[]): DocumentoAgrupado[] {
  const porDoc = new Map<number, Set<number>>()
  for (const f of fontes) {
    const paginas = porDoc.get(f.document_id) ?? new Set<number>()
    if (f.pagina != null) paginas.add(f.pagina)
    porDoc.set(f.document_id, paginas)
  }
  return Array.from(porDoc, ([document_id, paginas]) => ({
    document_id,
    paginas: Array.from(paginas).sort((a, b) => a - b),
  }))
}

const COR_ICONE = 'var(--mantine-color-inovareTeal-7)'
const DICA_INDISPONIVEL =
  'Você não tem acesso a este documento, ou ele foi removido da Base de Conhecimento.'

export interface FontesConsultadasProps extends FontesDaMensagem {
  /** Recuo à esquerda, para alinhar com os cartões do bot no chat de conversas. */
  ml?: number
}

export function FontesConsultadas({ fontes, total, ml }: FontesConsultadasProps) {
  const [aberto, setAberto] = useState(false)
  const [vendo, setVendo] = useState<{ id: number; pagina: number } | null>(null)
  const documentos = agrupar(fontes)
  const ids = documentos.map((d) => d.document_id)

  const titulos = useQuery({
    queryKey: ['kb-fontes', [...ids].sort((a, b) => a - b)],
    queryFn: () => resolverFontesKb(ids),
    enabled: aberto,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const porId = new Map((titulos.data ?? []).map((t) => [t.document_id, t]))
  const n = documentos.length

  return (
    <Box mt={4} ml={ml} maw={620} data-fontes-consultadas>
      <UnstyledButton
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-label="Fontes consultadas"
      >
        <Group gap={6} wrap="nowrap">
          <IconBooks size={14} color={COR_ICONE} aria-hidden />
          <Text size="xs" c="dimmed">
            Consultou <span style={{ fontFamily: FONTE_DADO }}>{n}</span>{' '}
            {n === 1 ? 'documento' : 'documentos'} da Base de Conhecimento
          </Text>
          {aberto ? (
            <IconChevronDown size={12} color={COR_ICONE} aria-hidden />
          ) : (
            <IconChevronRight size={12} color={COR_ICONE} aria-hidden />
          )}
        </Group>
      </UnstyledButton>

      {aberto && (
        <Stack gap={4} mt={4} pl={20}>
          {titulos.isLoading && <Loader size="xs" type="dots" color="inovareTeal" />}
          {titulos.isError && (
            <Text size="xs" c="dimmed">
              Não foi possível carregar os títulos agora.
            </Text>
          )}
          {titulos.data &&
            documentos.map((d) => {
              const t = porId.get(d.document_id)
              const disponivel = Boolean(t?.disponivel && t.titulo)
              return (
                <Group key={d.document_id} gap={6} wrap="nowrap" align="flex-start">
                  {disponivel ? (
                    <IconFileText size={13} color={COR_ICONE} aria-hidden style={{ marginTop: 3 }} />
                  ) : (
                    <IconLock size={13} color="var(--mantine-color-gray-6)" aria-hidden style={{ marginTop: 3 }} />
                  )}
                  {disponivel ? (
                    <UnstyledButton
                      onClick={() => setVendo({ id: d.document_id, pagina: d.paginas[0] ?? 1 })}
                      aria-label={`Abrir ${t!.titulo}${d.paginas.length ? ` na página ${d.paginas[0]}` : ''}`}
                      style={{ minWidth: 0 }}
                    >
                      <Text
                        size="sm"
                        c="inovareTeal.7"
                        td="underline"
                        style={{ overflowWrap: 'anywhere', textUnderlineOffset: 2 }}
                      >
                        {t!.titulo}
                      </Text>
                    </UnstyledButton>
                  ) : (
                    <Text
                      size="sm"
                      c="dimmed"
                      title={DICA_INDISPONIVEL}
                      style={{ minWidth: 0, overflowWrap: 'anywhere' }}
                    >
                      Documento indisponível
                    </Text>
                  )}
                  {d.paginas.length > 0 && (
                    <Text
                      component="span"
                      size="0.8rem"
                      c="dimmed"
                      style={{ fontFamily: FONTE_DADO, whiteSpace: 'nowrap', marginTop: 1 }}
                    >
                      p. {d.paginas.join(', ')}
                    </Text>
                  )}
                </Group>
              )
            })}
          {total != null && (
            <Text size="xs" c="dimmed" data-fontes-cortadas>
              Lista cortada: <span style={{ fontFamily: FONTE_DADO }}>{fontes.length}</span> de{' '}
              <span style={{ fontFamily: FONTE_DADO }}>{total}</span> registros de documento e página.
            </Text>
          )}
        </Stack>
      )}
      {vendo && (
        <VisualizadorDeDocumento
          documentId={vendo.id}
          paginaInicial={vendo.pagina}
          onClose={() => setVendo(null)}
        />
      )}
    </Box>
  )
}
